import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideAuth,
  authInterceptor,
  LogLevel,
} from 'angular-auth-oidc-client';
import { es_ES, NZ_I18N, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import { es } from 'date-fns/locale';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { companyInterceptor } from './core/interceptors/company.interceptor';
import { languageInterceptor } from './core/interceptors/language.interceptor';
import { timezoneInterceptor } from './core/interceptors/timezone.interceptor';
import { unauthorizedInterceptor } from './core/interceptors/unauthorized.interceptor';
import { GlobalLoadingService } from './core/services/global-loading.service';
import { provideAuthSession } from './core/providers/auth.providers';
import { ThemeService } from './core/services/theme.service';

registerLocaleData(localeEs, 'es');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEn, 'en-US');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptors([
        authInterceptor(),
        unauthorizedInterceptor,
        companyInterceptor,
        languageInterceptor,
        timezoneInterceptor,
      ]),
    ),
    provideAuth({
      config: {
        authority: environment.production
          ? 'https://sso.mojito360.com'
          : 'https://sso.pre.mojito360.com',
        redirectUrl: window.location.origin + '/auth/callback',
        postLogoutRedirectUri: window.location.origin,
        clientId: 'mojitobi_web',
        scope: 'mojitobi_api openid profile email offline_access',
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        // Applies to the ACCESS token only (see triggerRefreshWhenIdTokenExpired
        // below). `access_token_expires_at` is computed client-side as
        // `Date.now() + expires_in * 1000`, so this window is immune to a skewed
        // client clock.
        renewTimeBeforeTokenExpiresInSeconds: 120,
        // MUST stay false. When true, `areAuthStorageTokensValid()` also demands
        // `id_token.exp > Date.now() + renewTimeBeforeTokenExpiresInSeconds`.
        // `exp` is absolute server time while `Date.now()` is the client clock and
        // the library adds no skew tolerance, so a clock running fast by more than
        // (id_token lifetime - 120s) makes every freshly issued token look expired.
        // The route guard's only reaction is a full `login()` redirect, which the
        // IdP answers instantly from its session cookie -> infinite
        // authorize/callback loop. The id_token is an authentication receipt, not
        // the credential used to call the backend; its expiry must not force a
        // re-login while the access token is still good.
        triggerRefreshWhenIdTokenExpired: false,
        // MUST stay true. The library rejects any id_token whose `iat` is more than
        // maxIdTokenIatOffsetAllowedInSeconds (default 120) away from the client
        // clock, in EITHER direction, and that rejection wipes the stored tokens
        // before throwing -> the same infinite authorize/callback loop, this time
        // triggered from the callback instead of the guard. A machine can be off by
        // hours (dead CMOS battery, mis-applied timezone, VM restored from a
        // snapshot), so widening the window would only make the failure rare.
        // Replay protection does not depend on this check: the nonce, `state`,
        // PKCE S256, the JWKS signature and iss/aud/exp are all still validated,
        // and the authorization code is single-use and short-lived at the IdP.
        disableIatOffsetValidation: true,
        silentRenewTimeoutInSeconds: 30,
        refreshTokenRetryInSeconds: 2,
        // Warn (not None) in production: this is the level at which the library
        // logs the two lines that identify a failed id_token validation, which is
        // what an authorize/callback loop looks like from the outside.
        logLevel: environment.production ? LogLevel.Warn : LogLevel.Debug,
        // Both were previously inherited from the library defaults. '/'
        // happened to work; '/unauthorized' did not exist as a route, so a
        // failed callback fell through to the '**' -> '/es' redirect and
        // straight back into the auth guard.
        postLoginRoute: '/',
        unauthorizedRoute: '/unauthorized',
        secureRoutes: [environment.baseBackendUrl],
      },
    }),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: NZ_I18N, useValue: es_ES },
    { provide: NZ_DATE_LOCALE, useValue: es },
    GlobalLoadingService,
    // Resolves the OIDC session before the router's first navigation, and
    // provides the auth-session, redirect-memory, attempt-counter and
    // token-refresh services that the guards and interceptor depend on.
    provideAuthSession(),
    // App-wide, not route-scoped: /auth/callback and /:lang/select-company
    // live outside the main layout and still need a theme.
    ThemeService,
  ],
};
